import React, { useState, useEffect, useCallback } from 'react';
import SideMenu from "../../component/SideMenu";
import Topbar from "../../component/Topbar";
import SettingNav from './SettingNav';
import TeamModal from './TeamModal';
import { LuUsers, LuShieldCheck, LuPlus } from "react-icons/lu";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import "../../asset/css/profile-info.css";
import { baseurl } from '../../baseurl';
import axios from 'axios';
import Swal from 'sweetalert2';

const Team = () => {
    const [fetching, setFetching] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [members, setMembers] = useState([]);
    const [selectedMember, setSelectedMember] = useState(null); // Track member for editing
    const [permissions, setPermissions] = useState({
        defaultRole: 'Estimator',
        canShare: true,
        canExport: true,
        canCreate: true,
        canDelete: false
    });

    const userToken = localStorage.getItem('userToken');
    const Toast = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });

    // 1. Function to fetch all team members
    const fetchTeamMembers = useCallback(async () => {
        try {
            const res = await axios.get(`${baseurl}getTeamMembers`, {
                headers: { Authorization: `Bearer ${userToken}` }
            });
            if (res.data.success) {
                setMembers(res.data.data);
            }
        } catch (err) {
            console.error("Fetch Team Error:", err);
            Toast.fire({ icon: 'error', title: 'Could not load team list' });
        }
    }, [userToken]);

    // 2. Initial Data Load
    useEffect(() => {
        const loadInitialData = async () => {
            setFetching(true);
            try {
                // Get Preferences
                const res = await axios.get(`${baseurl}getUserPreferences`, {
                    headers: { Authorization: `Bearer ${userToken}` }
                });
                if (res.data.data.team_settings) {
                    setPermissions(res.data.data.team_settings);
                }
                
                // Get Members
                await fetchTeamMembers();
            } catch (err) {
                console.error(err);
            } finally {
                setFetching(false);
            }
        };
        loadInitialData();
    }, [userToken, fetchTeamMembers]);

    // 3. Handle Add Button Click
    const handleAddClick = () => {
        setSelectedMember(null); // Ensure no edit data is present
        setIsModalOpen(true);
    };

    // 4. Handle Edit Button Click
    const handleEditClick = (member) => {
        setSelectedMember(member); // Load member data into state
        setIsModalOpen(true);
    };

    // 5. Handle Delete Member
    const handleDeleteMember = async (id, name) => {
        const confirm = await Swal.fire({
            title: 'Are you sure?',
            text: `Remove ${name} from your team?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4318FF',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, remove'
        });

        if (confirm.isConfirmed) {
            try {
                await axios.delete(`${baseurl}deleteTeamMember/${id}`, {
                    headers: { Authorization: `Bearer ${userToken}` }
                });
                Toast.fire({ icon: 'success', title: 'Member removed' });
                fetchTeamMembers(); 
            } catch (err) {
                Toast.fire({ icon: 'error', title: 'Failed to remove member' });
            }
        }
    };

    const handlePermissionChange = (e) => {
        const { name, value, type, checked } = e.target;
        setPermissions(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const savePermissions = async () => {
        setIsLoading(true);
        try {
            await axios.put(`${baseurl}updateUserPreferences`, {
                column: 'team_settings',
                data: permissions
            }, { headers: { Authorization: `Bearer ${userToken}` } });
            Toast.fire({ icon: 'success', title: 'Permissions updated' });
        } catch (err) {
            Toast.fire({ icon: 'error', title: 'Update failed' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="app-container">
            <Topbar />
            <div className="main-wrapper" style={{ background: '#F4F7FE' }}>
                <SideMenu />
                <div className="profile-content-container p-3" style={{ flex: 1, overflow: 'auto' }}>
                    <SettingNav />

                    <div className="row g-3 mt-2">
                        <div className="col-lg-6">
                            <div className="premium-card h-100">
                                <div className="section-header justify-content-between">
                                    <div className="d-flex align-items-center">
                                        <div className="icon-wrap security-icon me-3"><LuUsers /></div>
                                        <div>
                                            <h5 className="card-title">Team Members</h5>
                                            <p className="card-subtitle">Landscaping staff & roles</p>
                                        </div>
                                    </div>
                                    <button className="btn-save-changes" onClick={handleAddClick}>
                                        <LuPlus /> Add Member
                                    </button>
                                </div>

                                <div className="member-list-container mt-3">
                                    {fetching ? (
                                        <div className="text-center p-5">
                                            <div className="spinner-border text-primary" role="status"></div>
                                            <p className="mt-2 text-muted">Loading team...</p>
                                        </div>
                                    ) : members.length === 0 ? (
                                        <div className="text-center p-5 border rounded-3 bg-light">
                                            <p className="text-muted mb-0">No team members added yet.</p>
                                        </div>
                                    ) : (
                                        members.map((m) => (
                                            <div key={m.id} className="member-box-row">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="avatar-initials" style={{ background: '#4318FF', color: '#fff' }}>
                                                        {m.name?.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <span className="member-name-text d-block">{m.name}</span>
                                                        <span className="member-role-text text-muted" style={{ fontSize: '12px' }}>
                                                            {m.role} • <span className={m.status === 'active' ? 'text-success fw-bold' : 'text-warning fw-bold'}>
                                                                {m.status.toUpperCase()}
                                                            </span>
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="member-actions">
                                                    {/* Updated Edit Button */}
                                                    <button 
                                                        className="action-icon-btn" 
                                                        onClick={() => handleEditClick(m)}
                                                    >
                                                        <FaEdit size={13} color='#64748b' />
                                                    </button>
                                                    <button 
                                                        className="action-icon-btn delete" 
                                                        onClick={() => handleDeleteMember(m.id, m.name)}
                                                    >
                                                        <FaTrashAlt size={13} color='#64748b' />
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-6">
                            <div className="premium-card h-100">
                                <div className="section-header">
                                    <div className="icon-wrap security-icon"><LuShieldCheck /></div>
                                    <div>
                                        <h5 className="card-title">Team Permissions</h5>
                                        <p className="card-subtitle">Global team access levels</p>
                                    </div>
                                </div>
                                <div className="form-body">
                                    <div className="input-block mb-3">
                                        <label className="input-label">Default New Member Role</label>
                                        <select name="defaultRole" value={permissions.defaultRole} onChange={handlePermissionChange} className="form-control-premium form-select">
                                            <option>Estimator</option>
                                            <option>Field Manager</option>
                                            <option>Admin</option>
                                        </select>
                                    </div>
                                    <div className="checkbox-list">
                                        {['canShare', 'canExport', 'canCreate', 'canDelete'].map(p => (
                                            <label className="checkbox-item" key={p}>
                                                <input type="checkbox" name={p} checked={permissions[p]} onChange={handlePermissionChange} />
                                                <span className="checkmark"></span>
                                                <span className="checkbox-label">Team members {p.replace('can', 'can ')}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="card-footer-actions mt-auto">
                                    <button className="btn-save-changes w-100" onClick={savePermissions} disabled={isLoading}>
                                        {isLoading ? 'Saving...' : 'Update Permissions'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <TeamModal 
                isOpen={isModalOpen} 
                onClose={() => {
                    setIsModalOpen(false);
                    setSelectedMember(null);
                }} 
                onSuccess={fetchTeamMembers}
                editData={selectedMember} // Pass the member data to the modal
            />
        </div>
    );
};

export default Team;